import * as React from "react";
import styled, { css } from "styled-components";
import throttle from "lodash/throttle";

import { ScreenSize, isLarger } from "../../common/utils/media";
import { colors, media } from "../../common/utils/style";
import { NavElementProps } from "../../common/components/SectionNavigator";
import WithScreenSize, { ScreenSizeProps } from "../../common/components/WithScreenSize";

type OwnProps = NavElementProps & {
  navTitle: string | React.ReactNode;
  activeColor: string;
};

type Props = OwnProps & ScreenSizeProps;

interface State {
  positioned: boolean;
  fixed: boolean;
  top: number | null;
  bottom: number | null;
}

class SectionNav extends React.Component<Props, State> {
  private base: HTMLElement | null = null;
  private listeningScroll: boolean;
  private onScrollThrottled: () => void;

  constructor(props: Props) {
    super(props);
    this.listeningScroll = false;
    this.state = {
      positioned: false,
      fixed: false,
      top: 0,
      bottom: null,
    };
    this.onScrollThrottled = throttle(this.onScroll, 50);
  }

  public componentDidMount() {
    this.listenScrollIfWideView(this.props.screenSize);
  }

  public componentWillReceiveProps(nextProps: Props) {
    if (isLarger(nextProps.screenSize, this.props.screenSize)) {
      this.listenScrollIfWideView(nextProps.screenSize!);
    }
    if (nextProps.wrapperRef && nextProps.wrapperRef !== this.props.wrapperRef) {
      this.updatePositionState(nextProps.wrapperRef);
    }
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.onScrollThrottled);
  }

  public listenScrollIfWideView(screenSize: ScreenSize) {
    if (this.listeningScroll) {
      return;
    }
    if (isLarger(screenSize, "large", true)) {
      window.addEventListener("scroll", this.onScrollThrottled);
      this.listeningScroll = true;
    }
  }

  public onScroll = () => {
    this.updatePositionState();
  };

  public updatePositionState(wrapperRef = this.props.wrapperRef) {
    if (!wrapperRef) {
      return;
    }
    this.setState({ ...this.calcPositionState(wrapperRef), positioned: true });
  }

  public calcPositionState(wrapperRef: HTMLElement): State {
    if (!this.base) {
      return this.state;
    }
    const windowHeight = window.innerHeight;
    const baseHeight = this.base.clientHeight;
    const wrapperRect = wrapperRef.getBoundingClientRect();
    if (wrapperRect.top > windowHeight / 2 - baseHeight / 2) {
      return { ...this.state, fixed: false, top: 0, bottom: null };
    } else if (wrapperRect.bottom < windowHeight / 2 + baseHeight / 2) {
      return { ...this.state, fixed: false, top: null, bottom: 0 };
    }
    return { ...this.state, fixed: true, top: null, bottom: null };
  }

  public render() {
    const { sectionIds, currentSectionId, sectionNamesMap, navTitle, activeColor, onClick } = this.props;
    const { positioned, fixed, top, bottom } = this.state;
    return (
      <Base hidden={!positioned} fixed={fixed} top={top} bottom={bottom} innerRef={el => (this.base = el)}>
        <Header>{navTitle}</Header>
        <div>
          {sectionIds.map(sectionId => (
            <NavItem
              key={sectionId}
              sectionId={sectionId}
              displayName={sectionNamesMap[sectionId]}
              active={sectionId === currentSectionId}
              activeColor={activeColor}
              onClick={onClick}
            >
              {sectionNamesMap[sectionId]}
            </NavItem>
          ))}
        </div>
      </Base>
    );
  }
}

interface NavItemProps {
  sectionId: string;
  displayName: string;
  active: boolean;
  activeColor: string;
  onClick: (sectionId: string) => void;
}

// tslint:disable-next-line:max-classes-per-file
class NavItem extends React.PureComponent<NavItemProps> {
  public onClick = (e: React.MouseEvent<HTMLElement>) => {
    const { sectionId, onClick } = this.props;
    e.preventDefault();
    onClick(sectionId);
  };

  public render() {
    const { sectionId, displayName, active, activeColor } = this.props;
    return (
      <NavItemBase
        key={sectionId}
        href={`#${sectionId}`}
        active={active}
        activeColor={activeColor}
        onClick={this.onClick}
      >
        {displayName}
      </NavItemBase>
    );
  }
}

const MAIN_COLUMN_WIDTH = 980;
const OFFSET_FROM_MAIN_COLUMN = 140;

interface BaseStyleProps {
  hidden: boolean;
  fixed: boolean;
  top: number | null;
  bottom: number | null;
}

const Base = styled.div`
  display: none;
  @media ${media.greaterThan(MAIN_COLUMN_WIDTH + OFFSET_FROM_MAIN_COLUMN * 2)} {
    display: ${(props: BaseStyleProps) => (props.hidden ? "none" : "block")};
    z-index: 10;
    min-width: 90px;
    padding: 50px 0;
    left: calc((100% - ${MAIN_COLUMN_WIDTH}px) / 2 - ${OFFSET_FROM_MAIN_COLUMN}px);
    ${(props: BaseStyleProps) =>
      props.fixed
        ? css`
            position: fixed;
            top: 50%;
            transform: translateY(-50%);
          `
        : css`
            position: absolute;
            top: ${props.top === null ? "unset" : `${props.top}px`};
            bottom: ${props.bottom === null ? "unset" : `${props.bottom}px`};
          `};
  }
`;

const Header = styled.div`
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 17px;
`;

interface NavItemStyleProps {
  active: boolean;
  activeColor: string;
}

const NavItemBase = styled.a`
  display: block;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.27);
  margin-bottom: 3px;
  cursor: pointer;
  &:hover {
    text-decoration: none;
    color: ${colors.gray4};
  }
  &:last-child {
    margin-bottom: 0;
  }
  ${(props: NavItemStyleProps) =>
    props.active
      ? css`
          font-weight: 500;
          color: ${colors.gray4};
          &:before {
            content: "";
            display: inline-block;
            width: 6px;
            height: 6px;
            background-color: ${props.activeColor};
            border-radius: 50%;
            margin-right: 6px;
          }
        `
      : ""};
`;

const SectionNavWithScreenSize: React.SFC<OwnProps> = props => {
  return <WithScreenSize>{({ screenSize }) => <SectionNav {...props} screenSize={screenSize} />}</WithScreenSize>;
};

export default SectionNavWithScreenSize;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/SectionNav.tsx